import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '../../components/ui/sheet';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../../components/ui/tooltip';
import {
  Building2, Plus, Upload, Search, ArrowLeft, Check, X, Pencil,
  Loader2, ShieldAlert, Lock, Eye, EyeOff, RefreshCw, Download,
  AlertTriangle, CreditCard, FileUp,
} from 'lucide-react';

const getAuth = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const acctStr = localStorage.getItem('current_account');
    const account = acctStr ? JSON.parse(acctStr) : null;
    return { user, accountId: account?.id || null };
  } catch { return { user: {}, accountId: null }; }
};

export default function EntitiesPage() {
  const navigate = useNavigate();
  const { user, accountId } = getAuth();
  const isSuperAdmin = user?.is_super_admin;
  const isAdmin = user?.role === 'admin' || isSuperAdmin;

  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Edit drawer
  const [editEntity, setEditEntity] = useState(null);
  const [editTab, setEditTab] = useState('details'); // 'details' | 'ach'
  const [editForm, setEditForm] = useState({});
  const [achForm, setAchForm] = useState({});
  const [achVisible, setAchVisible] = useState(false);
  const [achLoading, setAchLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ legal_entity_name: '', ein: '', site_name: '' });

  // CSV upload
  const [uploadOpen, setUploadOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadPreview, setUploadPreview] = useState(null);

  const fetchEntities = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/ubm/accounts/${accountId}/legal-entities`);
      const list = Array.isArray(data) ? data : (data.entities || []);
      setEntities(list);
    } catch {
      toast.error('Failed to load entities');
    } finally { setLoading(false); }
  }, [accountId]);

  useEffect(() => { fetchEntities(); }, [fetchEntities]);

  const filtered = entities.filter(e => {
    if (statusFilter !== 'all' && e.status !== statusFilter) return false;
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      return (e.legal_entity_name || '').toLowerCase().includes(s)
        || (e.ein || '').toLowerCase().includes(s)
        || (e.site_name || '').toLowerCase().includes(s);
    }
    return true;
  });

  const achConfigured = entities.filter(e => e.ach_details?.has_data).length;
  const achMissing = entities.filter(e => !e.ach_details?.has_data && e.status === 'active').length;

  // ── Open edit drawer ──
  const openEdit = async (entity) => {
    setEditEntity(entity);
    setEditTab('details');
    setEditForm({
      legal_entity_name: entity.legal_entity_name || '',
      ein: entity.ein || '',
      status: entity.status || 'active',
    });
    setAchForm({});
    setAchVisible(false);
  };

  // ── Load ACH for entity ──
  const loadAch = async () => {
    if (!editEntity) return;
    setAchLoading(true);
    try {
      // TESTING MODE: No 2FA required. In production, this requires 2FA verification.
      const { data } = await api.get(`/ubm/accounts/${accountId}/legal-entities/${editEntity.id}/ach`);
      const ach = data.ach_details || {};
      setAchForm({
        bank_name: ach.bank_name || '',
        routing_number: ach.routing_number || '',
        account_number: ach.account_number || '',
        account_type: ach.account_type || 'checking',
        company_name: ach.company_name || '',
        company_id: ach.company_id || '',
      });
      setAchVisible(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load ACH details');
    } finally { setAchLoading(false); }
  };

  // ── Save entity details ──
  const saveDetails = async () => {
    if (!editEntity) return;
    setSaving(true);
    try {
      await api.put(`/ubm/accounts/${accountId}/legal-entities/${editEntity.id}`, editForm);
      toast.success('Entity updated');
      fetchEntities();
      setEditEntity(prev => ({ ...prev, ...editForm }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update');
    } finally { setSaving(false); }
  };

  // ── Save ACH ──
  const saveAch = async () => {
    if (!editEntity) return;
    if (!achForm.routing_number || !achForm.account_number) {
      toast.error('Routing and Account numbers are required');
      return;
    }
    setSaving(true);
    try {
      await api.put(`/ubm/accounts/${accountId}/legal-entities/${editEntity.id}/ach`, achForm);
      toast.success('ACH payment config saved');
      fetchEntities();
      setEditEntity(prev => ({ ...prev, ach_details: { has_data: true } }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save ACH');
    } finally { setSaving(false); }
  };

  // ── Create entity ──
  const handleCreate = async () => {
    if (!createForm.legal_entity_name) { toast.error('Entity name is required'); return; }
    setSaving(true);
    try {
      await api.post(`/ubm/accounts/${accountId}/legal-entities`, createForm);
      toast.success('Entity created');
      setCreateOpen(false);
      setCreateForm({ legal_entity_name: '', ein: '', site_name: '' });
      fetchEntities();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create');
    } finally { setSaving(false); }
  };

  // ── CSV Upload ──
  const handleCsvSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      const lines = text.split('\n').filter(l => l.trim());
      if (lines.length < 2) { toast.error('CSV must have header + data rows'); return; }
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      const rows = lines.slice(1).map(line => {
        const vals = line.split(',');
        const row = {};
        headers.forEach((h, i) => { row[h] = (vals[i] || '').trim(); });
        return row;
      });
      setUploadPreview({ headers, rows, total: rows.length });
    };
    reader.readAsText(file);
  };

  const submitCsvUpload = async () => {
    if (!uploadPreview?.rows?.length) return;
    setUploading(true);
    let created = 0, errors = 0;
    for (const row of uploadPreview.rows) {
      try {
        const entityData = {
          legal_entity_name: row.legal_entity_name || row.entity_name || row.name || '',
          ein: row.ein || '',
          site_name: row.site_name || row.property || '',
        };
        if (!entityData.legal_entity_name) { errors++; continue; }
        const { data: entity } = await api.post(`/ubm/accounts/${accountId}/legal-entities`, entityData);

        // If ACH fields exist, save them
        const routing = row.routing_number || row.routing || row.aba || '';
        const account = row.account_number || row.account || '';
        if (routing && account) {
          await api.put(`/ubm/accounts/${accountId}/legal-entities/${entity.id}/ach`, {
            bank_name: row.bank_name || row.bank || '',
            routing_number: routing,
            account_number: account,
            account_type: row.account_type || 'checking',
            company_name: row.company_name || entityData.legal_entity_name,
            company_id: row.company_id || row.ein || '',
          });
        }
        created++;
      } catch { errors++; }
    }
    setUploading(false);
    setUploadOpen(false);
    setCsvFile(null);
    setUploadPreview(null);
    toast.success(`Uploaded ${created} entities${errors ? `, ${errors} errors` : ''}`);
    fetchEntities();
  };

  if (!accountId) return <div className="p-8 text-center text-muted-foreground">No account selected</div>;

  return (
    <TooltipProvider delayDuration={200}>
    <div className="h-screen bg-background flex flex-col overflow-hidden" data-testid="entities-page">
      {/* Testing Mode Banner */}
      <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center gap-2 flex-shrink-0" data-testid="testing-mode-banner">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        <span className="text-xs text-amber-800 font-medium">
          TESTING MODE — ACH data is accessible without 2FA. For production: enable encryption + 2FA unlock for Org Admin and Super Admin.
        </span>
      </div>

      {/* Header */}
      <div className="border-b border-border bg-card px-4 py-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(-1)} data-testid="entities-back-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <Building2 className="h-5 w-5 text-violet-600" />
            <h1 className="text-lg font-bold" data-testid="entities-title">Entities & ACH Config</h1>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="ach-configured-badge">
              <CreditCard className="h-3 w-3 mr-1" /> {achConfigured} ACH Configured
            </Badge>
            {achMissing > 0 && (
              <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200" data-testid="ach-missing-badge">
                <AlertTriangle className="h-3 w-3 mr-1" /> {achMissing} Missing ACH
              </Badge>
            )}
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setUploadOpen(true)} data-testid="upload-csv-btn">
              <Upload className="h-3.5 w-3.5 mr-1" /> Upload CSV
            </Button>
            <Button size="sm" className="h-8 text-xs bg-violet-600 hover:bg-violet-700 text-white" onClick={() => setCreateOpen(true)} data-testid="add-entity-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add Entity
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={fetchEntities} data-testid="refresh-entities-btn">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="px-4 py-2 border-b border-border/50 flex items-center gap-3 flex-shrink-0">
        <div className="relative">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="h-8 w-64 pl-8 text-xs"
            placeholder="Search entity name, EIN, property..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            data-testid="entity-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-8 w-32 text-xs" data-testid="entity-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground ml-auto">{filtered.length} of {entities.length} entities</span>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-40"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
            <Building2 className="h-10 w-10 mb-2 opacity-30" />
            <p className="text-sm">{searchTerm ? 'No matching entities' : 'No entities found'}</p>
          </div>
        ) : (
          <Table data-testid="entities-table">
            <TableHeader>
              <TableRow className="bg-muted/30">
                <TableHead className="text-[10px] uppercase font-semibold w-8">#</TableHead>
                <TableHead className="text-[10px] uppercase font-semibold">Entity Name</TableHead>
                <TableHead className="text-[10px] uppercase font-semibold w-28">EIN</TableHead>
                <TableHead className="text-[10px] uppercase font-semibold">Property / Site</TableHead>
                <TableHead className="text-[10px] uppercase font-semibold w-24">Status</TableHead>
                <TableHead className="text-[10px] uppercase font-semibold w-28">ACH Config</TableHead>
                <TableHead className="text-[10px] uppercase font-semibold w-24">Source</TableHead>
                <TableHead className="text-[10px] uppercase font-semibold w-20">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((e, i) => (
                <TableRow key={e.id} className="text-xs hover:bg-muted/20 cursor-pointer" onClick={() => openEdit(e)} data-testid={`entity-row-${i}`}>
                  <TableCell className="text-muted-foreground">{i + 1}</TableCell>
                  <TableCell className="font-medium">{e.legal_entity_name || '\u2014'}</TableCell>
                  <TableCell className="font-mono text-[11px]">{e.ein || '\u2014'}</TableCell>
                  <TableCell className="text-muted-foreground">{e.site_name || '\u2014'}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-[10px] ${e.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-50 text-gray-500 border-gray-200'}`}>
                      {e.status || 'active'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {e.ach_details?.has_data ? (
                      <Badge className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-300 rounded-sm" data-testid={`ach-badge-${i}`}>
                        <Check className="h-3 w-3 mr-0.5" /> Configured
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-300 rounded-sm" data-testid={`ach-badge-${i}`}>
                        <X className="h-3 w-3 mr-0.5" /> Not Set
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-[10px]">{e.sync_source || 'manual'}</TableCell>
                  <TableCell>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(ev) => { ev.stopPropagation(); openEdit(e); }} data-testid={`edit-entity-btn-${i}`}>
                      <Pencil className="h-3.5 w-3.5 text-muted-foreground hover:text-blue-600" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Edit Drawer */}
      <Sheet open={!!editEntity} onOpenChange={(o) => { if (!o) { setEditEntity(null); setAchVisible(false); } }}>
        <SheetContent side="right" className="sm:max-w-[520px] w-[520px] flex flex-col p-0" data-testid="entity-edit-drawer">
          <SheetHeader className="px-6 pt-6 pb-3 border-b">
            <SheetTitle className="flex items-center gap-2 text-sm">
              <Building2 className="h-4 w-4 text-violet-600" />
              {editEntity?.legal_entity_name || 'Entity'}
            </SheetTitle>
          </SheetHeader>

          {/* Tabs */}
          <div className="flex border-b px-6">
            <button
              className={`px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${editTab === 'details' ? 'border-violet-600 text-violet-700' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
              onClick={() => setEditTab('details')}
              data-testid="tab-details"
            >
              Entity Details
            </button>
            <button
              className={`px-4 py-2.5 text-xs font-medium border-b-2 transition-colors flex items-center gap-1.5 ${editTab === 'ach' ? 'border-violet-600 text-violet-700' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
              onClick={() => { setEditTab('ach'); if (!achVisible) loadAch(); }}
              data-testid="tab-ach"
            >
              <Lock className="h-3 w-3" /> ACH Payment Config
              {editEntity?.ach_details?.has_data && <Check className="h-3 w-3 text-emerald-500" />}
            </button>
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {editTab === 'details' && (
              <>
                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">Entity Name *</label>
                  <Input className="h-9 text-sm" value={editForm.legal_entity_name || ''} onChange={e => setEditForm(p => ({ ...p, legal_entity_name: e.target.value }))} data-testid="edit-entity-name" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">EIN</label>
                    <Input className="h-9 text-sm font-mono" value={editForm.ein || ''} onChange={e => setEditForm(p => ({ ...p, ein: e.target.value }))} placeholder="XX-XXXXXXX" data-testid="edit-entity-ein" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">Status</label>
                    <Select value={editForm.status || 'active'} onValueChange={v => setEditForm(p => ({ ...p, status: v }))}>
                      <SelectTrigger className="h-9 text-sm" data-testid="edit-entity-status"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {editEntity?.site_name && (
                  <div>
                    <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">Linked Property</label>
                    <p className="text-sm">{editEntity.site_name}</p>
                  </div>
                )}
                {editEntity?.sync_source === 'mysql' && (
                  <p className="text-[10px] text-muted-foreground bg-muted/30 rounded p-2">
                    Synced from MySQL. Changes to name/EIN may be overwritten on next sync.
                  </p>
                )}
              </>
            )}

            {editTab === 'ach' && (
              <>
                {/* Testing mode reminder */}
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-800 flex items-start gap-2" data-testid="ach-testing-notice">
                  <ShieldAlert className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold">Testing Mode Active</p>
                    <p className="mt-0.5 text-[11px]">ACH data displayed without 2FA. In production, Org Admin and Super Admin will need 2FA to unlock, decrypt, and view/edit ACH config.</p>
                  </div>
                </div>

                {achLoading ? (
                  <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
                ) : !achVisible ? (
                  <div className="text-center py-6">
                    <Lock className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
                    <p className="text-xs text-muted-foreground mb-3">ACH details are encrypted</p>
                    <Button variant="outline" size="sm" className="text-xs" onClick={loadAch} data-testid="unlock-ach-btn">
                      <Eye className="h-3.5 w-3.5 mr-1" /> View / Edit ACH Config
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="col-span-2">
                        <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">Bank Name</label>
                        <Input className="h-9 text-sm" value={achForm.bank_name || ''} onChange={e => setAchForm(p => ({ ...p, bank_name: e.target.value }))} placeholder="e.g. Chase, Bank of America" data-testid="ach-bank-name" />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">Routing Number *</label>
                        <Input className="h-9 text-sm font-mono" value={achForm.routing_number || ''} onChange={e => setAchForm(p => ({ ...p, routing_number: e.target.value }))} placeholder="9 digits" maxLength={9} data-testid="ach-routing" />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">Account Number *</label>
                        <Input className="h-9 text-sm font-mono" value={achForm.account_number || ''} onChange={e => setAchForm(p => ({ ...p, account_number: e.target.value }))} data-testid="ach-account" />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">Account Type</label>
                        <Select value={achForm.account_type || 'checking'} onValueChange={v => setAchForm(p => ({ ...p, account_type: v }))}>
                          <SelectTrigger className="h-9 text-sm" data-testid="ach-type"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="checking">Checking</SelectItem>
                            <SelectItem value="savings">Savings</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">Company Name</label>
                        <Input className="h-9 text-sm" value={achForm.company_name || ''} onChange={e => setAchForm(p => ({ ...p, company_name: e.target.value }))} data-testid="ach-company-name" />
                      </div>
                      <div className="col-span-2">
                        <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">Company ID (EIN for batch header)</label>
                        <Input className="h-9 text-sm font-mono" value={achForm.company_id || ''} onChange={e => setAchForm(p => ({ ...p, company_id: e.target.value }))} placeholder="e.g. 1234567890" data-testid="ach-company-id" />
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" className="text-xs text-muted-foreground" onClick={() => setAchVisible(false)} data-testid="hide-ach-btn">
                      <EyeOff className="h-3 w-3 mr-1" /> Lock ACH Details
                    </Button>
                  </>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-border px-6 py-3 flex items-center justify-end gap-2">
            <Button variant="outline" size="sm" className="text-xs" onClick={() => { setEditEntity(null); setAchVisible(false); }}>Close</Button>
            {editTab === 'details' && (
              <Button size="sm" className="text-xs bg-violet-600 hover:bg-violet-700 text-white" onClick={saveDetails} disabled={saving} data-testid="save-entity-btn">
                {saving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Check className="h-3 w-3 mr-1" />} Save Details
              </Button>
            )}
            {editTab === 'ach' && achVisible && (
              <Button size="sm" className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={saveAch} disabled={saving} data-testid="save-ach-btn">
                {saving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <CreditCard className="h-3 w-3 mr-1" />} Save ACH Config
              </Button>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="create-entity-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sm"><Plus className="h-4 w-4" /> Add Entity</DialogTitle>
            <DialogDescription className="text-xs">Create a new legal entity manually.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase mb-1 block">Entity Name *</label>
              <Input className="h-9 text-sm" value={createForm.legal_entity_name} onChange={e => setCreateForm(p => ({ ...p, legal_entity_name: e.target.value }))} data-testid="create-entity-name" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase mb-1 block">EIN</label>
              <Input className="h-9 text-sm font-mono" value={createForm.ein} onChange={e => setCreateForm(p => ({ ...p, ein: e.target.value }))} placeholder="XX-XXXXXXX" data-testid="create-entity-ein" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase mb-1 block">Property / Site Name</label>
              <Input className="h-9 text-sm" value={createForm.site_name} onChange={e => setCreateForm(p => ({ ...p, site_name: e.target.value }))} data-testid="create-entity-site" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" className="text-xs" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button size="sm" className="text-xs bg-violet-600 hover:bg-violet-700 text-white" onClick={handleCreate} disabled={saving} data-testid="create-entity-submit">
              {saving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Plus className="h-3 w-3 mr-1" />} Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV Upload Dialog */}
      <Dialog open={uploadOpen} onOpenChange={(o) => { setUploadOpen(o); if (!o) { setCsvFile(null); setUploadPreview(null); } }}>
        <DialogContent className="sm:max-w-lg" data-testid="upload-csv-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sm"><FileUp className="h-4 w-4" /> Upload Entities CSV</DialogTitle>
            <DialogDescription className="text-xs">
              CSV headers: legal_entity_name, ein, site_name, routing_number, account_number, account_type, bank_name, company_name, company_id
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input type="file" accept=".csv" onChange={handleCsvSelect} className="text-xs" data-testid="csv-file-input" />
            {uploadPreview && (
              <div className="border rounded-md p-3 bg-muted/20">
                <p className="text-xs font-medium mb-2">{uploadPreview.total} rows detected</p>
                <div className="overflow-x-auto max-h-40 text-[10px]">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr>{uploadPreview.headers.map(h => <th key={h} className="border-b px-2 py-1 text-left font-semibold">{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {uploadPreview.rows.slice(0, 5).map((r, i) => (
                        <tr key={i}>{uploadPreview.headers.map(h => <td key={h} className="border-b px-2 py-1 truncate max-w-[120px]">{r[h] || '\u2014'}</td>)}</tr>
                      ))}
                    </tbody>
                  </table>
                  {uploadPreview.total > 5 && <p className="text-muted-foreground mt-1">...and {uploadPreview.total - 5} more</p>}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" className="text-xs" onClick={() => { setUploadOpen(false); setCsvFile(null); setUploadPreview(null); }}>Cancel</Button>
            <Button size="sm" className="text-xs bg-violet-600 hover:bg-violet-700 text-white" onClick={submitCsvUpload} disabled={uploading || !uploadPreview} data-testid="csv-upload-submit">
              {uploading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Upload className="h-3 w-3 mr-1" />} Upload {uploadPreview?.total || 0} Entities
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </TooltipProvider>
  );
}
